import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { buildPageRange } from './pagination-utils';

export interface PaginationProps {
  /** Numer biezacej strony, liczony od 1. */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** Ile numerow pokazac po obu stronach biezacego. */
  siblings?: number;
  className?: string;
}

/** Nawigacja miedzy stronami wynikow. */
export function Pagination({
  page,
  pageCount,
  onPageChange,
  siblings = 1,
  className,
}: PaginationProps) {
  if (pageCount <= 1) return null;

  const items = buildPageRange(page, pageCount, siblings);

  const buttonClasses =
    'inline-flex size-9 items-center justify-center rounded-lg border text-sm font-semibold transition-colors disabled:pointer-events-none disabled:opacity-40';

  return (
    <nav aria-label="Stronicowanie" className={cn('flex items-center gap-1.5', className)}>
      <button
        type="button"
        aria-label="Poprzednia strona"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        className={cn(buttonClasses, 'border-border-strong bg-surface text-fg-muted hover:bg-surface-hover')}
      >
        <ChevronLeft className="size-4" />
      </button>

      {items.map((item, index) =>
        item === '…' ? (
          <span key={`gap-${index}`} className="text-fg-subtle px-1 text-sm">
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            aria-label={`Strona ${item}`}
            aria-current={item === page ? 'page' : undefined}
            onClick={() => onPageChange(item)}
            className={cn(
              buttonClasses,
              item === page
                ? 'border-brand bg-brand text-fg-on-brand'
                : 'border-border-strong bg-surface text-fg-muted hover:bg-surface-hover',
            )}
          >
            {item}
          </button>
        ),
      )}

      <button
        type="button"
        aria-label="Następna strona"
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
        className={cn(buttonClasses, 'border-border-strong bg-surface text-fg-muted hover:bg-surface-hover')}
      >
        <ChevronRight className="size-4" />
      </button>
    </nav>
  );
}
